/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, ref } from 'vue';

export const useRequestStateMachine = <
  T,
  TRequest extends (...args: any[]) => Promise<any>,
>(config: {
  request: TRequest;
}) => {
  type RequestParameters = Parameters<TRequest>;
  type RequestResponseData = Awaited<ReturnType<TRequest>>;

  type State =
    | { state: 'IDLE' }
    | { state: 'LOADING' }
    | { state: 'SUCCESS'; data: RequestResponseData }
    | { state: 'ERROR'; error: Error | string };

  const state = ref<State>({ state: 'IDLE' });
  const data = ref<T>();

  const transitions = {
    goToIdleState: (): void => {
      state.value = { state: 'IDLE' };
    },
    goToLoadingState: (): void => {
      state.value = { state: 'LOADING' };
    },
    goToSuccessState: (data: RequestResponseData): void => {
      state.value = { state: 'SUCCESS', data };
    },
    goToErrorState: (error: Error | string): void => {
      state.value = { state: 'ERROR', error };
    },
  } as const;

  const reset = () => {
    transitions.goToIdleState();
    data.value = undefined;
  };

  let latestRequestId = 0;

  const fetchData = async (
    ...params: RequestParameters
  ): Promise<RequestResponseData | undefined> => {
    const requestId = ++latestRequestId;
    const isLatestRequest = () => requestId === latestRequestId;
    transitions.goToLoadingState();

    try {
      const response = await config.request(...params);

      if (isLatestRequest()) {
        transitions.goToSuccessState(response);
        data.value = response;
      }

      return response;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error : String(error);

      if (isLatestRequest()) {
        transitions.goToErrorState(errorMessage);
      }
      return undefined;
    }
  };

  const isIdle = computed(() => state.value.state === 'IDLE');
  const isLoading = computed(() => state.value.state === 'LOADING');
  const isSuccess = computed(() => state.value.state === 'SUCCESS');
  const isError = computed(() => state.value.state === 'ERROR');

  const error = computed(() => {
    if (!isError.value) return;
    if (state.value.state !== 'ERROR') return;

    return state.value.error;
  });

  return {
    state,
    data,
    fetchData,
    reset,
    isIdle,
    isLoading,
    isSuccess,
    isError,
    error,
  };
};
